import PropTypes from 'prop-types';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import { getCommunityScopeOptions } from 'shared/constants';
import { useTransl } from 'shared/lib/i18n';

import AsyncTableComponent from '../../Table/AsyncTableContainer';
import CommentApprovalContainer from './CommentApprovalContainer';

import ExpandableImageComponent from '../../Image/ExpandableImageComponent';
import PriceComponent from '../components/PriceComponent';
import ApprovalStatusComponent from './ApprovalStatusComponent';

import {
  checkHasPermissions,
  formatDate,
  toSentenceCase,
} from '../../../utils';
import { getFullName } from '../../Person/utils';
import { getExportTitle } from '../../ViewExport/utils';

import { TABLE_CONSTANTS, TABLE_ID } from '../../Table/constants';
import { COMMUNITY_POST_TYPES } from '../constants';

import permissions from '../../../permissions';
import { DATE_RANGES_TYPES, TABLE_DATA_TYPES } from '../../../staticData';
import CellPersonWithImage from '../../Table/CellPersonWithImage';
import {
  communityFilter,
  communityStatusFilter,
  limitFilter,
  typeFilter,
} from '../../Table/filters';
import PopperTooltip from '../../Tooltip';
import CommunityApprovalExport from './CommunityApprovalExport';

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    marginBottom: '-3px !important',
  },
}));

const CommunityApprovalComponent = (props) => {
  const { t } = useTransl();
  const {
    onLoad,
    items,
    communityList,
    getItems,
    listParams,
    listKey,
    onExport,
    onUpdatePostStatus,
    openPostModal,
    onRemovePost,
    expandedByDefault,
  } = props;

  const columns = [
    {
      Header: t('title.community'),
      id: 'community',
      headerClassName: 'sort',
      minWidth: 160,
      accessor: ({ community }) => community?.name,
    },
    {
      Header: t('title.type'),
      id: 'type',
      headerClassName: 'sort',
      width: 150,
      accessor: ({ type }) => toSentenceCase(type),
    },
    {
      Header: t('title.post'),
      id: 'post',
      sortable: false,
      minWidth: 200,
      accessor: (entry, isExport) => {
        const { photos, text, price, type } = entry;
        if (isExport) {
          let description = '';
          if (type === COMMUNITY_POST_TYPES.marketplace && price)
            description += `$${price} • `;
          if (text) description += text;
          return description;
        }

        return (
          <Box display="flex" alignItems="center">
            {photos?.length > 0 && (
              <Box mr="1rem">
                <ExpandableImageComponent
                  src={photos?.[0]?.url}
                  height="3.2rem"
                  isSquare
                />
              </Box>
            )}
            {type === COMMUNITY_POST_TYPES.marketplace && (
              <PriceComponent price={price} />
            )}
            <PopperTooltip placement="top" trigger="hover" tooltip={text}>
              {text}
            </PopperTooltip>
          </Box>
        );
      },
    },

    {
      Header: t('title.postedOn'),
      id: 'created_at',
      headerClassName: 'sort',
      width: 180,
      accessor: ({ createdAt }) => formatDate(createdAt),
    },
    {
      Header: t('title.postedBy'),
      id: 'sender',
      headerClassName: 'sort',
      width: 200,
      accessor: (entry, isExport) => {
        const { sender } = entry;
        if (isExport) return getFullName(sender);
        return (
          <CellPersonWithImage
            person={sender}
            linkUrl={`/global-person-search/${sender?.id}`}
          />
        );
      },
    },
    {
      Header: t('title.unit'),
      id: 'unit',
      headerClassName: 'sort',
      width: 150,
      accessor: (entry) => {
        const { sender } = entry;
        return sender?.propertyName ? sender?.propertyName : '';
      },
    },
    {
      Header: t('title.subject'),
      id: 'subject',
      headerClassName: 'sort',
      width: 150,
      accessor: (entry) => {
        const { subject } = entry;
        return subject;
      },
    },
    {
      expander: true,
      Header: t('title.pendingApprovalComments'),
      id: 'pending_approval_comments',
      sortable: false,
      width: 130,
      // eslint-disable-next-line react/prop-types
      Expander: ({ isExpanded, original: { commentsRequireApproval } }) => {
        let icon = <ExpandMoreIcon />;
        let tooltipTitle = t('title.expandMore');

        if (isExpanded) {
          icon = <ExpandLessIcon />;
          tooltipTitle = t('title.expandLess');
        } else if (!commentsRequireApproval && !isExpanded) {
          icon = <RemoveIcon />;
        }

        return (
          <StyledTooltip title={tooltipTitle} placement="top" arrow>
            <Box
              role="presentation"
              sx={{ display: 'flex', justifyContent: 'center', height: '5rem' }}
            >
              {icon}
            </Box>
          </StyledTooltip>
        );
      },
    },
    {
      Header: t('title.status'),
      id: 'approval_status',
      sortable: false,
      width: 150,
      show: checkHasPermissions(permissions.approve_community_post),
      accessor: ({ id, approvalStatus }, isExport) =>
        isExport ? (
          toSentenceCase(approvalStatus)
        ) : (
          <ApprovalStatusComponent
            id={id}
            approvalStatus={approvalStatus}
            isExport={isExport}
            onUpdate={onUpdatePostStatus}
            onRemove={onRemovePost}
          />
        ),
    },
  ];

  const filters = [
    communityFilter(listParams, communityList),
    typeFilter(listParams, getCommunityScopeOptions().slice(1)),
    limitFilter(listParams),
    communityStatusFilter(listParams),
  ];

  const onRowClick = (rowInfo) => {
    const currentPost = rowInfo?.original;
    openPostModal(currentPost);
  };

  const rowStyles = {
    style: {
      cursor: 'pointer',
    },
  };

  const defaultParams = {
    building: null,
    q: null,
    created_at_after: null,
    created_at_before: null,
    type: null,
    ordering: 'created_at',
    approval_status: 'all_pending',
  };

  const action = null;
  const tableDataType = TABLE_DATA_TYPES.posts;

  const title = t('title.contentApproval');
  const exportTitle = getExportTitle(title);
  const exportActions = (
    <CommunityApprovalExport
      title={exportTitle}
      getDataForExport={onExport}
      columns={columns}
    />
  );

  return (
    <AsyncTableComponent
      tableId={TABLE_ID.communityApproval}
      title={title}
      columns={columns}
      defaultSort={TABLE_CONSTANTS.created_at}
      filters={filters}
      showFiltersAreApplied={false}
      onLoad={onLoad}
      items={items}
      getItems={getItems}
      listParams={listParams}
      defaultParams={defaultParams}
      tableDataType={tableDataType}
      listKey={listKey}
      onRowClick={onRowClick}
      rowStyles={rowStyles}
      dateRangeType={DATE_RANGES_TYPES.created_at}
      datePicker
      action={action}
      manual
      defaultSortDesc
      exportActions={exportActions}
      searchAutoFocus={false}
      SubComponent={(row) => <CommentApprovalContainer post={row?.original} />}
      expandedByDefault={expandedByDefault}
    />
  );
};

CommunityApprovalComponent.propTypes = {
  communityList: PropTypes.instanceOf(Array).isRequired,
  onLoad: PropTypes.func.isRequired,
  getItems: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onUpdatePostStatus: PropTypes.func.isRequired,
  openPostModal: PropTypes.func.isRequired,
  onRemovePost: PropTypes.func.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  listKey: PropTypes.string.isRequired,
  expandedByDefault: PropTypes.instanceOf(Object).isRequired,
};

export default CommunityApprovalComponent;
